import { clsx } from 'clsx';
import { type AriaAttributes, createContext, type ReactNode, useContext, useId } from 'react';

type FieldContextValue = { id: string; describedBy: string | undefined; invalid: boolean; required: boolean };

const FieldContext = createContext<FieldContextValue | null>(null);

type ControlWiring = Pick<AriaAttributes, 'aria-describedby' | 'aria-invalid'> & { id?: string; required?: boolean };

/**
 * Wiring an input placed inside a `<Field>`: id (matches the label), aria-describedby (hint/error),
 * aria-invalid and required. Explicit props on the control win.
 */
export function useFieldControl(props: ControlWiring): ControlWiring {
  const field = useContext(FieldContext);
  return {
    id: props.id ?? field?.id,
    'aria-describedby': props['aria-describedby'] ?? field?.describedBy,
    'aria-invalid': props['aria-invalid'] ?? (field?.invalid || undefined),
    required: props.required ?? (field?.required || undefined),
  };
}

export type FieldProps = {
  label: ReactNode;
  /** Help text under the control (hidden while an error is shown). */
  hint?: ReactNode;
  /** Validation message; marks the control invalid. */
  error?: string | null;
  required?: boolean;
  /** Renders the label visually hidden (still read by screen readers). */
  hideLabel?: boolean;
  /** Override the generated control id. */
  id?: string;
  className?: string;
  /** Right side of the label row, e.g. a "Generate" link. */
  labelAside?: ReactNode;
  children: ReactNode;
};

/** Label + control + hint/error. Controls from this design system pick up ids and aria automatically. */
export function Field({ label, hint, error, required = false, hideLabel, id, className, labelAside, children }: FieldProps) {
  const generated = useId();
  const controlId = id ?? `field-${generated}`;
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;
  const describedBy = error ? errorId : hint ? hintId : undefined;

  return (
    <FieldContext.Provider value={{ id: controlId, describedBy, invalid: Boolean(error), required }}>
      <div className={clsx('flex flex-col gap-1.5', className)}>
        <div className={clsx('flex items-center justify-between gap-2', hideLabel && 'sr-only')}>
          <label htmlFor={controlId} className="text-[13px] font-medium text-slate-700">
            {label}
            {required && (
              <span className="ml-0.5 text-ember-600" aria-hidden>
                *
              </span>
            )}
          </label>
          {labelAside}
        </div>
        {children}
        {error ? (
          <p id={errorId} className="text-xs text-red-600">
            {error}
          </p>
        ) : hint ? (
          <p id={hintId} className="text-xs text-slate-500">
            {hint}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}
